import {FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../contexts/AppContext';
import Menu from './Menu';
import LanguagePopup from './PopupLanguage';
import VolumeControl from './VolumeControl';
import BlockError from '../components/BlockError';
import { u } from '../common/Units';
import { getMenuDisplayMode } from '../common/MenuDisplay';
import HomeIcon from "../assets/icon_home.svg?react";
import BackIcon from "../assets/icon_back.svg?react";
import MenuIcon from "../assets/icon_menu.svg?react";
import LargerFontIcon from "../assets/icon_font_larger.svg?react";
import SmallerFontIcon from "../assets/icon_font_smaller.svg?react";
import LanguageIcon from "../assets/icon_language.svg?react";
import VolumeLowIcon from "../assets/icon_volume_low.svg?react";
import VolumeMidIcon from "../assets/icon_volume_mid.svg?react";
import VolumeHighIcon from "../assets/icon_volume_high.svg?react";
import ContrastIcon from "../assets/icon_contrast.svg?react";
import { COLOR_SCHEMA_ID } from '../Settings';

interface HeaderProps {
  title?: string,
  backgroundColor?: string,
  hideBackButton?: boolean,
  /**
   * Hide title and buttons, but keep a persistent (left/bottom) menu.
   * Without a persistent menu, the back button is still shown so the user can leave the view.
   */
  hideBar?: boolean,
  /** Override the text and icon color, e.g. when a view picks a readable color from its background. Optional */
  textColor?: string,
  /** Custom action for the back button instead of going back in history, e.g. inside a view in preview. Optional */
  onBack?: () => void
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Wrapper = styled.header<{ $textColor?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  padding: ${u(30)} ${u(25)} 0 ${u(25)};
  box-sizing: border-box;
  float: left;
  touch-action: none;
  height: ${u(120)};
  pointer-events: none;
  display: flex;
  align-items: center;
  align-content: center;
  ${p => p.$textColor ? `color: ${p.$textColor};\n  & a, & a svg { fill: ${p.$textColor}; }` : ""}

  &.preview a {
    pointer-events: none;
  }

  /* Buttons with their own action inside the view (onBack) still work in preview */
  &.preview a.previewEnabled {
    pointer-events: all;
  }

  &.light {
    background: linear-gradient(180deg, rgba(255 255 255 / .1) 0, transparent);

    & h1 {
      text-shadow: 1px 1px 5px rgb(255 255 255 / 30%);
    }

    & a {
      filter: drop-shadow(1px 1px 5px rgba(255 255 255 / 30%));
    }
  }

  &.dark {
    background: linear-gradient(180deg, rgba(0 0 0 / .2) 0, transparent);

    & h1 {
      text-shadow: 1px 1px 5px rgb(0 0 0 / 50%);
    }

    & a {
      filter: drop-shadow(1px 1px 5px rgba(0 0 0 / 50%));
    }
  }

  /* Only the back button is shown: no gradient over the content, and a round button that stands out */
  &.backOnly {
    background: none;

    & a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${u(60)};
      height: ${u(60)};
      border-radius: 50%;
      background-color: ${p => p.theme?.backgroundColor || "#000"};
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12), 0 4px 8px rgba(0, 0, 0, 0.18), 0 12px 32px rgba(0, 0, 0, 0.18);
      filter: none;
    }

    & a svg {
      width: ${u(26)};
      height: ${u(26)};
      /* The chevron is drawn to the left of its box; nudge it to look centered */
      margin-left: calc(-2 * var(--u));
    }
  }
`;

const Title = styled.h1`
  position: absolute;
  left: 50%;
  width: calc(100% - 300px);
  font-size: 1.8em;
  font-weight: 600;
  text-align: center;
  margin: 0 0 5px;
  transform: translateX(-50%);
  max-width: 70%;
  --max-lines: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;

  &.noBackButton {
    /* left: ${u(100)};
    transform: translateX(0); */
  }

  &.fadeIn {
    animation: ${fadeIn} 0.2s;
  }
`

const LeftButtonContainer = styled.div`
  position: absolute;
  left: ${u(65)};
  text-align: left;

  & a:not(:first-child) {
    margin-left: ${u(50)};
  }
`;

const RightButtonContainer = styled.div`
  position: absolute;
  right: ${u(65)};
  text-align: right;

  & a:not(:last-child) {
    margin-right: ${u(50)};
  }
`;

const Button = styled.a`
  fill: ${p => p.theme?.textColor};
  pointer-events: all;
  cursor: pointer;
  position: relative;
  display: inline-block;

  & svg {
    width: ${u(32)};
    height: ${u(32)};
    fill: ${p => p.theme.navigationColor || "#ffffff"};
  }

  &:active {
    opacity: .8;
  }

  &&::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
  }
`

const Warning = styled.div`
  bottom: 30px;
  position: fixed;
  left: 30px;
  z-index: 100;
  max-width: 960px;
  box-shadow: 0 0px 15px rgba(0, 0, 0, 0.2);
  font-size: 1.2em;
  pointer-events: none;

  & div {
    margin-bottom: 0;
  }
`;

/**
 * Render the app header. Consists of a back button, a title and the menu button
 * @returns {JSX.Element} Component template
 */
const Header: FC<HeaderProps> = ({title, backgroundColor, hideBackButton, hideBar, textColor, onBack}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { previewIsActive, deviceId, deviceMenu, deviceData, deviceLanguages, globalFontSize, setGlobalFontSize, globalVolume, hideHeader, setMediaIsPlaying, theme, previewSchemaId } = useContext(AppContext);
  const titleRef = useRef<any>(null);
  const volumeRef = useRef<any>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [languagePopupOpen, setLanguagePopupOpen] = useState(false);
  const [displayLanguageIcon, setDisplayLanguageIcon] = useState<boolean>(false);
  const [displayFontIcon, setDisplayFontIcon] = useState<boolean>(false);
  const [displayMenuIcon, setDisplayMenuIcon] = useState<boolean>(false);
  const [displayVolumeIcon, setDisplayVolumeIcon] = useState<boolean>(false);
  const [displayHomeIcon, setDisplayHomeIcon] = useState<boolean>(false);
  const [displayContrastIcon, setDisplayContrastIcon] = useState<boolean>(false);
  const [colorMode, setColorMode] = useState<string>("dark");
  const [displayVolumeControl, setDisplayVolumeControl] = useState<boolean>(false);
  const [currentContrast, setCurrentContrast] = useState<number>(0);
  const headerIsHidden = Boolean(hideHeader && !previewIsActive);
  const menuDisplayMode = headerIsHidden ? null : getMenuDisplayMode(deviceData, deviceMenu);

  /**
   * Go back in history
   */
  const goBack = useCallback(() => {
    setMediaIsPlaying(false);
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  }, [navigate, setMediaIsPlaying, onBack]);

  /**
   * Go back in history
   */
  const goHome = useCallback(() => {
    deviceId && navigate(`/device/${deviceId}`);
  }, [navigate, deviceId]);

  /**
   * Toggle contrast
   */
  const toggleContrast = useCallback(() => {
    console.log("Contrast button not yet implemented");
  }, []);

  /**
   * Toggle volume control
   */
  const toggleVolume = useCallback(() => {
    setDisplayVolumeControl(true);
  }, []);

  /**
   * Returns true if current page is home
   */
  const isHome = /device\/(\d+)(\/)?$/.test(window?.location?.hash)

  /**
   * Toggle font size
   */
  const toggleFontSize = useCallback(() => {
    if (globalFontSize === 1) {
      setGlobalFontSize(1.2);
    } else {
      setGlobalFontSize(1);
    }
  }, [globalFontSize, setGlobalFontSize]);

  /**
   * Generate volume icon with gain indicator
   * @returns {JSX.Element} Volume icon element
   */
  const VolumeIcon = useCallback(() => {
    if (!globalVolume) { return <VolumeLowIcon/> }

    if (globalVolume > .8) {
      return <VolumeHighIcon/>
    } else if (globalVolume > .2) {
      return <VolumeMidIcon/>
    } else {
      return <VolumeLowIcon/>
    }
  }, [globalVolume]);

  // Should icons be displayed?
  useEffect(() => {
    setDisplayMenuIcon(menuDisplayMode === "overlay");
    if ((deviceLanguages as string[])?.length > 1) {
      setDisplayLanguageIcon(true);
    } else {
      setDisplayLanguageIcon(false);
    }
  }, [deviceLanguages, isHome, menuDisplayMode]);

  useEffect(() => {
    if (deviceData?.accessibility?.toggleFontSize) {
      setDisplayFontIcon(true);
    } else {
      setDisplayFontIcon(false);
    }

    if (deviceData?.accessibility?.showHomeIcon) {
      setDisplayHomeIcon(true);
    } else {
      setDisplayHomeIcon(false);
    }

    if (deviceData?.accessibility?.showContrastIcon) {
      setDisplayContrastIcon(true);
    } else {
      setDisplayContrastIcon(false);
    }

    if (deviceData?.accessibility?.showVolumeIcon) {
      setDisplayVolumeIcon(true);
    } else {
      setDisplayVolumeIcon(false);
    }
  }, [deviceData]);

  // Animate title fade when title is updated
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.classList.remove("fadeIn");
      setTimeout(() => {
        titleRef.current.classList.add("fadeIn");
      }, 200);
    }
  }, [title])

/**
 * Determine contrast between foreground and background colors
 * Sets dark or light color mode for the header
 */
const determineContrast = useCallback((foregroundColor: string, backgroundColor: string) => {
  const parseColor = (color: string): [number, number, number] | null => {
    if (color.startsWith("#")) {
      const hex = color.length === 4 ? color.slice(1).split("").map(c => parseInt(c + c, 16)) : [0, 1, 2].map(i => parseInt(color.slice(1 + i * 2, 3 + i * 2), 16));
      return hex.length === 3 ? [hex[0], hex[1], hex[2]] : null;
    } else {
      const match = color.match(/\d+/g)?.slice(0, 3).map(Number);
      return match && match.length === 3 ? [match[0], match[1], match[2]] : null;
    }
  };

  const luminance = (rgb: [number, number, number]): number => {
    const [r, g, b] = rgb.map(c => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const fg = parseColor(foregroundColor);
  const bg = parseColor(backgroundColor);

  // Fallback
  if (!fg || !bg) {
    setColorMode("dark");
    return;
  }

  const lumFg = luminance(fg);
  const lumBg = luminance(bg);

  // WCAG contrast ratio formula
  const contrastRatio = (Math.max(lumFg, lumBg) + 0.05) / (Math.min(lumFg, lumBg) + 0.05);
  setCurrentContrast(contrastRatio);

  // Based on WCAG: >= 4.5 is good for normal text
  setColorMode(lumBg > lumFg ? "light" : "dark");
}, []);



  // Check background color to determine if dark or light mode should be used
  useEffect(() => {
    const textColorTemp = textColor || theme?.textColor;
    const backgroundColorTemp = backgroundColor || theme?.backgroundColor || "#000";
    //const navigationColorTemp = theme?.navigationColor;

    determineContrast(textColorTemp || "#fff", backgroundColorTemp);
  }, [backgroundColor, textColor, theme, determineContrast]);

  if (headerIsHidden) {
    return null;
  }

  // With the bar hidden, keep the back button unless a left/bottom menu offers another way out
  const hasPersistentMenu = menuDisplayMode === "left" || menuDisplayMode === "bottom";
  const backOnly = Boolean(hideBar && !hasPersistentMenu);

  return (
    <>
      {(!hideBar || backOnly) && (<Wrapper $textColor={textColor} className={`${colorMode} ${previewIsActive ? "preview" : ""}${backOnly ? " backOnly" : ""}`}>
        {(!isHome && !hideBackButton) && (
          <LeftButtonContainer>
            <Button onClick={goBack} className={onBack ? "previewEnabled" : undefined}><BackIcon/></Button>
            {displayHomeIcon && !backOnly && (<Button onClick={goHome}><HomeIcon/></Button>)}
          </LeftButtonContainer>
        )}
        {title && !backOnly && (<Title className={`fadeIn${isHome ? " noBackButton" : ""}`}>{title}</Title>)}
        {!backOnly && (<RightButtonContainer>
          {displayLanguageIcon && (<Button className={`showInPreview`} onClick={() => {setLanguagePopupOpen(true)}}><LanguageIcon/></Button> )}
          {displayContrastIcon && (<Button className={`showInPreview`} onClick={toggleContrast}><ContrastIcon/></Button> )}
          {displayVolumeIcon && (<Button ref={volumeRef} className={`showInPreview`} onClick={toggleVolume}><VolumeIcon/></Button> )}
          {displayFontIcon && (<Button className={`showInPreview`} onClick={toggleFontSize}>{globalFontSize === 1 ? (<LargerFontIcon/>) : (<SmallerFontIcon/>)}</Button> )}
          {displayMenuIcon && (<Button className={`showInPreview`} onClick={() => {setMenuOpen(true)}}><MenuIcon/></Button>)}
        </RightButtonContainer>)}
      </Wrapper>)}
      <Menu displayMode={menuDisplayMode} isOpen={menuDisplayMode === "left" || menuOpen} close={() => {setMenuOpen(false)}}/>
      {displayVolumeControl && (<VolumeControl x={volumeRef?.current?.getBoundingClientRect()?.x + (volumeRef?.current?.getBoundingClientRect()?.width / 2)} y={volumeRef?.current?.getBoundingClientRect()?.y + volumeRef?.current?.getBoundingClientRect()?.height} close={() => { setDisplayVolumeControl(false) }}/>)}
      {displayLanguageIcon && (<LanguagePopup isOpen={languagePopupOpen} close={() => {setLanguagePopupOpen(false)}}/>)}
      {(previewSchemaId === COLOR_SCHEMA_ID && currentContrast < 4.5) && <Warning><BlockError text={t("contrasts.warning").replace("{contrast}", String(currentContrast.toFixed(2)))} includeWrapper={false}/></Warning>}
    </>
  );
}

export default Header;